import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, ShoppingBag, ArrowRight, X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatPrice } from '@/lib/utils';
import { CartItem } from '@/types';

interface AddedToCartModalProps {
  open: boolean;
  onClose: () => void;
  product: {
    id: number;
    produt_name: string;
    sellingprice: string;
    images: string[];
  };
  quantity: number;
  selectedVariations?: Array<{ variation: string; quantity: number }>;
  onContinueShopping: () => void;
  onGoToCart: () => void;
}

export function AddedToCartModal({
  open,
  onClose,
  product,
  quantity,
  selectedVariations,
  onContinueShopping,
  onGoToCart,
}: AddedToCartModalProps) {
  const totalQuantity = selectedVariations && selectedVariations.length > 0
    ? selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
    : quantity || 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Header */}
            <div className="bg-white p-6 text-gray-900 relative overflow-hidden border-b border-gray-200">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="absolute top-4 right-4"
              >
                <button
                  onClick={onClose}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors backdrop-blur-sm"
                >
                  <X className="h-5 w-5" />
                </button>
              </motion.div>
              
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", duration: 0.6 }}
                className="flex items-center justify-center mb-4"
              >
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-full">
                  <CheckCircle className="h-10 w-10" />
                </div>
              </motion.div>
              
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-bold text-center"
              >
                Added to Cart!
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center text-gray-500 mt-2"
              >
                Item successfully added to your cart.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="text-center text-gray-500 mt-1 text-sm"
              >
                Try our latest arrivals or proceed to cart.
              </motion.p>
            </div>

            {/* Product Details */}
            <div className="p-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="w-full"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">Product Added to Cart</h3>

                <div className="space-y-2 text-gray-800">
                  {selectedVariations && selectedVariations.length > 0 ? (
                    selectedVariations
                      .filter((variation) => variation.quantity > 0)
                      .map((variation, index) => (
                        <div key={index} className="flex justify-between items-center gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                          <span className="text-sm font-semibold text-gray-900">x{variation.quantity}</span>
                          <span className="text-sm truncate">{variation.variation}</span>
                        </div>
                      ))
                  ) : (
                    <div className="flex justify-between items-center gap-3 border border-gray-200 rounded-lg px-3 py-2 bg-gray-50">
                      <span className="text-sm font-semibold text-gray-900">x{totalQuantity}</span>
                      <span className="text-sm truncate">{product.produt_name}</span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Action Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <Button
                  onClick={onContinueShopping}
                  className="h-12 text-base font-semibold border-2 border-brand-200 text-white bg-brand-600 hover:bg-brand-700 transition"
                >
                  Continue Shopping
                </Button>

                <Button
                  onClick={onGoToCart}
                  className="h-12 text-base font-semibold text-white bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-500 transition"
                >
                  <ShoppingBag className="h-5 w-5 mr-2 inline-block" />
                  Go to Cart
                  <ArrowRight className="h-4 w-4 ml-2 inline-block" />
                </Button>
              </motion.div>
            </div>

            {/* Floating particles animation */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ 
                    opacity: 0,
                    scale: 0,
                    x: Math.random() * 400,
                    y: Math.random() * 300
                  }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    y: [0, -50, -100]
                  }}
                  transition={{
                    duration: 2,
                    delay: 0.5 + i * 0.1,
                    ease: "easeOut"
                  }}
                  className="absolute w-2 h-2 bg-brand-300 rounded-full"
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}